import { useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw, Sparkles, Wand2 } from 'lucide-react';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';
import { cvPrincipal, type Candidato } from '@/lib/candidatos';
import { useCatalogos } from './catalogos';

/**
 * Lo que la IA ha sacado del currículum.
 *
 * La lectura la dispara la subida del CV, no este panel: al llegar aquí lo
 * normal es que ya esté hecha, o en marcha. Mientras está en marcha se enseña
 * el aviso de «leyendo» y la ficha va consultando cada cuatro segundos.
 *
 * El volcado sobre la ficha solo toca campos VACÍOS, y eso vale igual cuando
 * ocurre solo al subir el CV que cuando se pulsa el botón: una ficha corregida
 * a mano no puede perderse porque alguien vuelva a leer el currículum.
 */
interface Props {
  candidato: Candidato;
  onActualizado: (c: Candidato) => void;
}

export default function PanelIA({ candidato, onActualizado }: Props) {
  const { catalogos } = useCatalogos();
  const [leyendo, setLeyendo] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const ia = candidato.ia;
  const tieneCv = !!cvPrincipal(candidato);
  const leyendoDeFondo = ia?.estado === 'leyendo';

  const leer = async () => {
    setLeyendo(true);
    setError(null);
    setAviso(null);
    try {
      onActualizado(await api.leerCvConIa(candidato.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido leer el currículum');
    } finally {
      setLeyendo(false);
    }
  };

  const aplicar = async () => {
    setAplicando(true);
    setError(null);
    try {
      const { candidato: actualizado, rellenados } = await api.aplicarPropuestaIa(candidato.id);
      onActualizado(actualizado);
      setAviso(
        rellenados.length === 1
          ? 'Se ha rellenado 1 campo.'
          : `Se han rellenado ${rellenados.length} campos.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se ha podido rellenar la ficha');
    } finally {
      setAplicando(false);
    }
  };

  if (!tieneCv) return null;

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-2 mb-3">
        <h2 className="font-medium flex items-center gap-2">
          <Sparkles size={17} className="text-sereno-dark/50" />
          Lectura del currículum
        </h2>
        {ia && !leyendoDeFondo && (
          <button
            onClick={() => void leer()}
            disabled={leyendo}
            className="btn-ghost h-9 text-sm flex items-center gap-1.5 flex-shrink-0"
            title="Volver a leer el CV"
          >
            {leyendo ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <RefreshCw size={14} />
            )}
            <span className="hidden sm:inline">Volver a leer</span>
          </button>
        )}
      </div>

      {leyendoDeFondo && (
        <div className="flex items-start gap-3">
          <Loader2 size={18} className="animate-spin text-sereno-dark/40 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium">Leyendo el currículum…</p>
            <p className="text-sm text-sereno-dark/50 mt-0.5">
              Tarda entre veinte segundos y un minuto. Puedes seguir a lo tuyo: la ficha se
              actualiza sola en cuanto termine.
            </p>
          </div>
        </div>
      )}

      {!ia && (
        <div className="space-y-3">
          <p className="text-sm text-sereno-dark/60">
            Saca de su CV el nombre, el contacto, la localidad, el carnet, los idiomas y la
            experiencia, y propone los puestos que encajan. Los currículums que se suban a partir
            de ahora se leen solos; este botón es para los de antes.
          </p>
          <button
            onClick={() => void leer()}
            disabled={leyendo}
            className="btn-primary h-11 text-sm flex items-center gap-2"
          >
            {leyendo ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {leyendo ? 'Leyendo el CV… tarda medio minuto' : 'Leer el CV con IA'}
          </button>
        </div>
      )}

      {ia && !leyendoDeFondo && ia.estado !== 'ok' && (
        <div className="card bg-amber-50 border-amber-200 py-3">
          <p className="text-sm text-amber-900 flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            <span>
              {ia.estado === 'sin_texto'
                ? 'Este PDF no lleva texto dentro: es un escaneado o una foto metida en un PDF. '
                : 'No se ha podido leer el currículum. '}
              {ia.detalle}
            </span>
          </p>
          {ia.estado === 'sin_texto' && (
            <p className="text-sm text-amber-900/80 mt-2">
              Haz una foto del CV con el móvil y súbela: de una foto sí sabe leer.
            </p>
          )}
        </div>
      )}

      {ia && ia.estado === 'ok' && (
        <div className="space-y-4">
          {ia.resumen && <p className="text-sm">{ia.resumen}</p>}

          {ia.experiencia.length > 0 && (
            <div>
              <h3 className="text-xs uppercase tracking-wide text-sereno-dark/40 mb-1">
                Experiencia
              </h3>
              <ul className="text-sm space-y-0.5">
                {ia.experiencia.map((linea, i) => (
                  <li key={i}>{linea}</li>
                ))}
              </ul>
            </div>
          )}

          {ia.puestosSugeridos.length > 0 && (
            <div>
              <h3 className="text-xs uppercase tracking-wide text-sereno-dark/40 mb-1">
                Puestos que propone
              </h3>
              <div className="flex flex-wrap gap-1">
                {ia.puestosSugeridos.map((p) => (
                  <span
                    key={p}
                    className="inline-flex items-center px-2 py-0.5 rounded-md bg-sereno-blue/50 text-sereno-dark text-xs font-medium"
                  >
                    {catalogos.puestoLabel[p] || p}
                  </span>
                ))}
              </div>
            </div>
          )}

          {ia.puntosFuertes.length > 0 && (
            <div>
              <h3 className="text-xs uppercase tracking-wide text-sereno-dark/40 mb-1">
                Puntos fuertes
              </h3>
              <ul className="text-sm list-disc pl-4 space-y-0.5">
                {ia.puntosFuertes.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          )}

          {ia.alertas.length > 0 && (
            <div className="card bg-amber-50 border-amber-200 py-3">
              <h3 className="text-xs uppercase tracking-wide text-amber-800 mb-1">
                A tener en cuenta
              </h3>
              <ul className="text-sm text-amber-900 list-disc pl-4 space-y-0.5">
                {ia.alertas.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </div>
          )}

          <button
            onClick={() => void aplicar()}
            disabled={aplicando}
            className="btn-secondary h-11 text-sm flex items-center gap-2"
          >
            {aplicando ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
            Rellenar los campos vacíos
          </button>
          <p className="text-xs text-sereno-dark/40">
            Al leer el CV los campos vacíos se rellenan solos. Este botón vuelve a intentarlo con
            los que sigan en blanco; no toca nada de lo que ya esté escrito.
          </p>

          {aviso && <p className="text-sm text-green-700">{aviso}</p>}

          <p className="text-xs text-sereno-dark/30 pt-2 border-t border-gray-100">
            Leído el {formatDateTime(ia.at)} con {ia.modelo}
            {ia.idiomaCv && ia.idiomaCv !== 'desconocido' && ` · CV en ${ia.idiomaCv}`}
          </p>
        </div>
      )}

      {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
    </div>
  );
}
